import { cm } from 'src/utils/classNames';
import { BigNum } from '@velocity-exchange/sdk';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';

interface QuickActionButtonProps extends WithClassName, WithChildren {
	onClick: () => void;
	dataTestId?: string;
}

const QuickActionButton = (props: QuickActionButtonProps) => {
	return (
		<button
			type="button"
			className={cm(
				'flex items-center gap-x-xs rounded-full px-2 py-2xs',
				'bg-button-secondary-bg-hover typo-body-2xs text-text-default',
				props.className
			)}
			onClick={props.onClick}
			data-testid={props.dataTestId}
		>
			{props.children}
		</button>
	);
};

const FiftyPercentButton = (props: {
	onClick: () => void;
	dataTestId?: string;
}) => (
	<QuickActionButton onClick={props.onClick} dataTestId={props.dataTestId}>
		50%
	</QuickActionButton>
);

const MaxButton = (props: {
	onClick: () => void;
	maxAmount: BigNum;
	isLoading?: boolean;
	dataTestId?: string;
}) => (
	<QuickActionButton onClick={props.onClick} dataTestId={props.dataTestId}>
		<span>Max:</span>
		{props.isLoading ? (
			<SkeletonValuePlaceholder className="h-3 w-10" loading />
		) : (
			props.maxAmount.toMillified()
		)}
	</QuickActionButton>
);

const CustomLabelButton = (props: {
	onClick: () => void;
	label: string;
	amount?: BigNum;
	isLoading?: boolean;
	dataTestId?: string;
}) => (
	<QuickActionButton onClick={props.onClick} dataTestId={props.dataTestId}>
		<span>{props.label}</span>
		{props.amount !== undefined &&
			(props.isLoading ? (
				<SkeletonValuePlaceholder className="h-3 w-12" loading />
			) : (
				props.amount.toMillified()
			))}
	</QuickActionButton>
);

const SizePresets = (props: {
	onClick: (amount: BigNum) => void;
	maxAmount: BigNum;
	isLoading?: boolean;
	/**
	 * Stamps `<prefix>-half` and `<prefix>-max` test ids on the two buttons.
	 * Defaults to `collateral`.
	 */
	testIdPrefix?: string;
}) => {
	const prefix = props.testIdPrefix ?? 'collateral';

	return (
		<div className="flex items-center gap-1">
			<FiftyPercentButton
				onClick={() => props.onClick(props.maxAmount.div(BigNum.from(2)))}
				dataTestId={`${prefix}-half`}
			/>
			<MaxButton
				onClick={() => props.onClick(props.maxAmount)}
				maxAmount={props.maxAmount}
				isLoading={props.isLoading}
				dataTestId={`${prefix}-max`}
			/>
		</div>
	);
};

export const InputQuickActions = {
	FiftyPercentButton,
	MaxButton,
	CustomButton: CustomLabelButton,
	SizePresets,
};
